import { useCallback, useEffect, useState } from "react";
import { MessagesSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { TerminalShell } from "@/components/TerminalShell";
import { ChatThread } from "@/components/chat/ChatThread";
import { chatService } from "@/services/chatService";
import { errorMessage } from "@/services/apiClient";

const POLL_MS = 10000;

export default function SupportChatPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const openCount = items.filter((c) => !["resolvido", "fechado"].includes(c.status)).length;
  const limitReached = openCount >= 2;

  const loadList = useCallback(async () => {
    try {
      const data = await chatService.conversations();
      setItems(data);
      return data;
    } catch {
      return [];
    }
  }, []);

  const openConversation = useCallback(async (id) => {
    try {
      setActive(await chatService.conversation(id));
      loadList();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [loadList]);

  useEffect(() => {
    loadList().then((data) => {
      if (data.length) openConversation(data[0].id);
    });
  }, [loadList, openConversation]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadList();
      setActive((current) => {
        if (current) chatService.conversation(current.id).then(setActive).catch(() => {});
        return current;
      });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [loadList]);

  const send = async (body) => {
    try {
      await chatService.send(active.id, body);
      await openConversation(active.id);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const created = await chatService.create({ subject: subject.trim(), message: message.trim() });
      setSubject("");
      setMessage("");
      setCreating(false);
      await loadList();
      openConversation(created.id);
      toast.success("Conversa aberta com o suporte");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <TerminalShell>
      <div data-testid="support-chat-page" className="p-4 sm:p-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-light tracking-tight text-white">Suporte</h1>
            <p className="mt-1 text-sm text-[#8A97AD]">
              Fale com a nossa equipa. Apenas mensagens de texto — o histórico é mantido 30 dias.
            </p>
          </div>
          <button
            data-testid="new-conversation-button"
            onClick={() => setCreating((v) => !v)}
            disabled={limitReached}
            className="flex items-center gap-2 rounded-full bg-[#00A884] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#029072] disabled:opacity-40"
          >
            <Plus className="h-4 w-4" /> Nova conversa
          </button>
        </div>

        {limitReached ? (
          <p data-testid="conversation-limit-note" className="mb-6 rounded-md border border-[#141A26] chat-card p-3 text-sm chat-meta">
            Já tem 2 conversas em aberto. Continue a escrever numa delas — pode abrir uma nova
            quando o suporte resolver ou fechar uma das atuais.
          </p>
        ) : null}

        {creating ? (
          <form onSubmit={create} data-testid="new-conversation-form" className="mb-6 space-y-3 rounded-md border border-[#141A26] chat-card p-4">
            <input
              data-testid="new-conversation-subject"
              value={subject}
              maxLength={120}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Assunto"
              className="w-full rounded-md border px-3 py-2 text-sm outline-none chat-field"
            />
            <textarea
              data-testid="new-conversation-message"
              value={message}
              rows={3}
              maxLength={600}
              onChange={(e) => setMessage(e.target.value.slice(0, 600))}
              placeholder="Descreva a sua questão (máx. 600 caracteres)"
              className="w-full resize-none rounded-md border px-3 py-2 text-sm outline-none chat-field"
            />
            <button
              type="submit"
              data-testid="new-conversation-submit"
              disabled={!subject.trim() || !message.trim()}
              className="rounded-full bg-[#00A884] px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              Abrir conversa
            </button>
          </form>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <div data-testid="conversation-list" className="space-y-2">
            {items.length === 0 ? (
              <p className="rounded-md border border-[#141A26] chat-card p-4 text-sm chat-meta">
                Sem conversas. Abra uma nova para falar com o suporte.
              </p>
            ) : null}
            {items.slice(0, 4).map((c) => (
              <button
                key={c.id}
                data-testid={`conversation-${c.id}`}
                onClick={() => openConversation(c.id)}
                className={`w-full rounded-md border px-3 py-3 text-left text-sm transition-colors ${
                  active?.id === c.id ? "border-[#00A884]/60 chat-card" : "border-[#141A26] chat-card hover:opacity-90"
                }`}
              >
                <p className="flex items-center gap-2 truncate font-medium chat-title">
                  <MessagesSquare className="h-4 w-4 text-[#00A884]" /> {c.subject}
                </p>
                <p className="mt-1 truncate text-xs chat-meta">{c.last_message_preview}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-[#00A884]">
                  {c.status_label}
                  {c.unread > 0 ? ` · ${c.unread} nova(s)` : ""}
                </p>
              </button>
            ))}
          </div>

          {active ? (
            <ChatThread conversation={active} staffView={false} onSend={send} />
          ) : (
            <div className="rounded-md border border-[#141A26] chat-card p-6 text-sm chat-meta">
              Selecione uma conversa.
            </div>
          )}
        </div>
      </div>
    </TerminalShell>
  );
}
